import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Ruler, Loader2, Plus, Trash2, Save } from "lucide-react";

const eur = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));

export default function Progettazione({ embedded = false }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/settings/progettazione").then((r) => setTiers(r.data.tiers || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setRow = (i, k, v) => setTiers((t) => t.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addRow = () => setTiers((t) => [...t, { fino_a_kw: "", prezzo: "", note: "" }]);
  const removeRow = (i) => setTiers((t) => t.filter((_, idx) => idx !== i));

  const save = async () => {
    const payload = { tiers: tiers.map((t) => ({ fino_a_kw: Number(t.fino_a_kw), prezzo: Number(t.prezzo), note: t.note || "" })) };
    if (payload.tiers.some((t) => !t.fino_a_kw || Number.isNaN(t.fino_a_kw) || Number.isNaN(t.prezzo))) {
      toast.error("Compila potenza e prezzo per ogni fascia");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/progettazione", payload);
      setTiers(data.tiers);
      toast.success("Listino progettazione salvato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {!embedded && (
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Progettazione</h1>
          <p className="text-xs text-muted-foreground">Listino costi di progettazione a corpo per fascia di potenza</p>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-progettazione-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva listino
        </Button>
      </header>
      )}

      <div className={embedded ? "" : "p-4 lg:p-8"}>
        {embedded && (
          <div className="flex justify-end mb-4">
            <Button onClick={save} disabled={saving || loading} data-testid="save-progettazione-btn" className="font-semibold">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva listino
            </Button>
          </div>
        )}
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <Card className="rounded border-border p-5" data-testid="progettazione-card">
            <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
              <Ruler className="h-4 w-4 text-primary" strokeWidth={1.5} /> Fasce di potenza
            </h2>
            <p className="text-xs text-muted-foreground mb-5">Il costo di progettazione viene applicato automaticamente al preventivo in base alla potenza dimensionata (kWp). Viene scelta la prima fascia il cui limite ≥ potenza impianto.</p>

            <div className="hidden sm:grid grid-cols-[140px_140px_1fr_40px] gap-3 px-1 pb-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
              <div>Fino a (kW)</div><div>Prezzo</div><div>Descrizione</div><div></div>
            </div>

            <div className="space-y-2">
              {tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-[140px_140px_1fr_40px] gap-3 items-center" data-testid={`tier-row-${i}`}>
                  <div className="space-y-1 sm:space-y-0">
                    <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Fino a (kW)</Label>
                    <Input type="number" value={t.fino_a_kw} onChange={(e) => setRow(i, "fino_a_kw", e.target.value)} data-testid={`tier-kw-${i}`} placeholder="20" />
                  </div>
                  <div className="space-y-1 sm:space-y-0">
                    <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo (€)</Label>
                    <Input type="number" value={t.prezzo} onChange={(e) => setRow(i, "prezzo", e.target.value)} data-testid={`tier-prezzo-${i}`} placeholder="750" />
                  </div>
                  <div className="col-span-2 sm:col-span-1 space-y-1 sm:space-y-0">
                    <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</Label>
                    <Input value={t.note || ""} onChange={(e) => setRow(i, "note", e.target.value)} data-testid={`tier-note-${i}`} placeholder="Impianti fino a 20 kW" />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeRow(i)} data-testid={`tier-remove-${i}`} className="text-destructive hover:text-destructive justify-self-end col-span-2 sm:col-span-1">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline" size="sm" onClick={addRow} data-testid="add-tier-btn" className="mt-4">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi fascia
            </Button>

            <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground">
              Esempio: {tiers.length > 0 && `un impianto da ${tiers[0].fino_a_kw} kW costa ${eur(tiers[0].prezzo)} di progettazione.`}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
